import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { WkdUploadCore, message } from '@common/musicHubCommon';
import type { UploadFileInfo } from '@common/musicHubCommon';
import { ROUTES } from '../../../routes';
import { reportPageView } from '../../../services/reportLog';
import './index.scss';

const UPLOAD_ICON = 'https://akoss.bangcdn.net/fe/Upload.svg';
const MUSIC_ICON = 'https://akoss.bangcdn.net/fe/MusicBig.svg';

const ReleaseUpload: React.FC = () => {
  const navigate = useNavigate();
  const [uploadedFile, setUploadedFile] = useState<UploadFileInfo | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingFileName, setUploadingFileName] = useState('');

  useEffect(() => {
    reportPageView('content_0011', { withArtistId: true });
  }, []);

  const handleProgress = (_percent: number, file: any) => {
    setIsUploading(true);
    setUploadingFileName(file?.name || '');
  };

  // 处理上传成功
  const handleUploadSuccess = (fileInfo: UploadFileInfo) => {
    setIsUploading(false);
    setUploadedFile(fileInfo);
    
    // 保存文件信息到 sessionStorage（包含 url 和 md5）
    sessionStorage.setItem('releaseAudioFile', JSON.stringify({
      name: fileInfo.name,
      url: fileInfo.url,
      md5: fileInfo.md5 || '',
    }));
    
    message.success('Upload completed!');
    
    // 延迟跳转到下一步
    setTimeout(() => {
      navigate(ROUTES.releaseBasicInfo);
    }, 800);
  };

  // 处理上传失败
  const handleUploadFailed = (error: string) => {
    setIsUploading(false);
    message.error(error || 'Upload failed, please try again');
  };

  const handleBack = () => {
    navigate(ROUTES.releaseType);
  };

  return (
    <div className="release-upload">
      {/* 页面标题 */}
      <div className="release-upload__header">
        <button className="back-btn" onClick={handleBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
          </svg>
          <span>Upload Audio</span>
        </button>
      </div>

      {/* 主内容 */}
      <div className="release-upload__content">
        <div className="release-upload__card">
          {/* 标题 */}
          <h1 className="release-upload__title">Upload Audio</h1>

          {/* 上传区域 */}
          <WkdUploadCore
            action="/api/cosUpload"
            dir="songs"
            reqParam={{ originalPath: 'true' }}
            accept=".wav,.mp3,.m4a,audio/wav,audio/mpeg,audio/mp3,audio/x-m4a,audio/m4a"
            size={200 * 1024}
            disabled={isUploading}
            onChange={handleUploadSuccess}
            onFaild={handleUploadFailed}
            onProgress={handleProgress}
          >
            <div
              className={`upload-zone ${!uploadedFile && !isUploading ? 'upload-zone--clickable' : ''} ${
                isUploading ? 'upload-zone--uploading' : ''
              }`}
            >
              {uploadedFile || isUploading ? (
                // 上传中 / 上传完成（共用进度条结构）
                <div className="upload-zone__uploading">
                  <div className="upload-zone__icon">
                    <img src={MUSIC_ICON} alt="" />
                  </div>
                  <div className="upload-zone__text">
                    <h2>{uploadedFile ? 'Upload Complete' : 'Uploading...'}</h2>
                    <p>WAV, MP3, M4A accepted. Max 200MB per file.</p>
                  </div>
                  <div className="upload-zone__progress">
                    <div
                      className={`progress-bar ${isUploading ? 'progress-bar--loading' : ''}`}
                    >
                      <div
                        className="progress-bar__fill"
                        style={{ width: uploadedFile ? '100%' : undefined }}
                      />
                    </div>
                    <div className="progress-info">
                      <span className="progress-info__name">
                        {uploadedFile ? uploadedFile.name : uploadingFileName}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                // 空状态
                <div className="upload-zone__empty">
                  <div className="upload-zone__icon">
                    <img src={UPLOAD_ICON} alt="" />
                  </div>
                  <div className="upload-zone__text">
                    <h2>Click to upload music file or drop it here</h2>
                    <p>WAV, MP3, M4A accepted. Max 200MB per file.</p>
                  </div>
                </div>
              )}
            </div>
          </WkdUploadCore>
        </div>
      </div>
    </div>
  );
};

export default ReleaseUpload;
